import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useForm, FormProvider } from 'react-hook-form'

import { Tag, Tooltip } from 'antd'
import styled from 'styled-components'
import produce from 'immer'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { colours } from '@qflow/theme'

import { useRecordDetailData } from 'portal/components/RecordDetails'
import {
  LimitWidthContainer,
  MainSection
} from 'portal/components/RecordsModules/common/StyledComponents'

import {
  ApplyOrCancelControls,
  FormItem,
  useMixpanelEvents
} from 'portal/components/RecordEditing'

import { convertAssignedToArrayToString, imperial } from 'portal/lib/functions'
import {
  useModuleIds,
  useMountEffect,
  useUserProjectRole
} from 'portal/lib/hooks'
import {
  ColourButton,
  Flex,
  Spinner,
  SubHeading,
  Text
} from 'portal/lib/primitives'
import { EditPencil } from 'portal/lib/primitives/icons'

import { useRecordEditing } from 'portal/store/records/editing/useRecordEditing'
import { InfractionsContext } from 'portal/store/infractions/InfractionsContext'
import { projectGetDetailsSaga } from 'portal/store/projectDetails/actions'
import { projectUsersLoad } from 'portal/store/projectUsers/actions'

import { ActionStrip } from './ActionStrip'
import { Delivery } from './Delivery'
import { Waste, cleanWasteRecordForUpdate } from './Waste'

const MODULE_VIEWS = {
  delivery: Delivery,
  waste: Waste
}

const EDIT_TITLES = {
  delivery: 'Edit Record',
  waste: 'Edit Waste'
}

export const RecordEdit = ({ doneUri }) => {
  const dispatch = useDispatch()
  const { accountId, projectId } = useModuleIds()
  const { isProjectUser, isProjectAdmin, roleLoaded } = useUserProjectRole(
    accountId,
    projectId
  )
  const navigate = useNavigate()

  const [actionStripRecordID, setActionStripRecordID] = useState([])
  const [actionStripMode, setActionStripMode] = useState(null)
  const [assignedTo, setAssignedTo] = useState('')
  const [recordAssigneeMapping, setRecordAssigneeMapping] = useState({})
  const [recordTagsMapping, setRecordTagsMapping] = useState({})

  const { moduleName } = useModuleIds()
  const moduleKey = moduleName.toLowerCase()
  const ModuleView = MODULE_VIEWS[moduleKey]

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )
  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )
  const isImperial = imperial(projectCountry)

  // Check if we need to refresh project details
  useEffect(() => {
    if (!projectTimeZone || !projectCountry) {
      if (accountId && projectId) {
        dispatch(projectGetDetailsSaga(accountId, projectId))
      }
    }
  }, [accountId, dispatch, projectCountry, projectId, projectTimeZone])

  const editing = useRecordEditing()
  const recordDetail = useRecordDetailData()

  const loading = recordDetail.loading || editing.uploading
  const error = recordDetail.error || editing.error

  const { form, trackEditCancel, trackEditSave } = useFormWithDataAndTracking(
    !recordDetail.loading && !recordDetail.error,
    recordDetail.item
  )

  const infractionsByName =
    recordDetail && recordDetail.infractions
      ? structuredClone(recordDetail.infractions)
      : null

  // Remove infractions that have a non-Open status
  if (infractionsByName) {
    Object.keys(infractionsByName).forEach(fieldName => {
      if (
        infractionsByName[fieldName] &&
        infractionsByName[fieldName].status !== 'Open'
      ) {
        delete infractionsByName[fieldName]
      }
    })
  }

  const { onEditSaveComplete, cancelUri } = useEditCompleteRedirection(doneUri)

  const openActionStrip = mode => {
    if (recordDetail.item && recordDetail.item.recordId) {
      setActionStripMode(mode)
      setActionStripRecordID([recordDetail.item.recordId])
    }
  }

  const closeActionStrip = () => {
    setActionStripMode(null)
    setActionStripRecordID([])
  }

  const submitItem = useCallback(
    formItem => {
      let item = formItem
      if (typeof ModuleView.transformFromFormData === 'function') {
        item = ModuleView.transformFromFormData(formItem, projectTimeZone)
      }

      const record = produce(recordDetail.item, draftRecord => {
        _.merge(draftRecord.data, item.data)
      })

      const cleanedRecord = cleanWasteRecordForUpdate(record)

      trackEditSave()

      editing.submitRecord(cleanedRecord, onEditSaveComplete, isImperial)
    },
    [
      ModuleView,
      editing,
      isImperial,
      onEditSaveComplete,
      projectTimeZone,
      recordDetail.item,
      trackEditSave
    ]
  )

  // Load Project Users in to Redux state
  useEffect(() => {
    if (roleLoaded && isProjectUser) {
      dispatch(projectUsersLoad(accountId, projectId, false))
    }
  }, [accountId, dispatch, isProjectUser, projectId, roleLoaded])

  useEffect(() => {
    if (recordDetail.item && !recordDetail.item.canEdit) {
      navigate(doneUri)
    }

    if (recordDetail.item && recordDetail.item.assignedTo) {
      setAssignedTo(
        convertAssignedToArrayToString(recordDetail.item.assignedTo)
      )
      const newObject = {}
      newObject[recordDetail.item.recordId] = recordDetail.item.assignedTo

      setRecordAssigneeMapping(newObject)
    }

    if (recordDetail.item && recordDetail.item.recordId) {
      const tagsObject = {}
      tagsObject[recordDetail.item.recordId] = recordDetail.item.tags || []
      setRecordTagsMapping(tagsObject)
    }
  }, [doneUri, navigate, recordDetail.item])

  return (
    <LimitWidthContainer>
      {loading && <Spinner />}
      {error && (
        <Text error>Error! {typeof error === 'string' ? error : ''}</Text>
      )}

      {recordDetail.item && (
        <MainSection>
          <ActionStrip
            recordIds={actionStripRecordID}
            recordAssigneeMapping={recordAssigneeMapping}
            recordTagsMapping={recordTagsMapping}
            openMode={actionStripMode}
            onClose={closeActionStrip}
            onApply={onEditSaveComplete}
          />

          <FormProvider {...form}>
            <Form onSubmit={form.handleSubmit(submitItem)}>
              <Flex.Row amount={0} verticalCentre>
                <SubHeading
                  id="RecordEditHeading"
                  isChild
                  style={{ fontSize: '1.125rem', color: colours.CORE_NAVY }}
                >
                  {EDIT_TITLES[moduleKey]}
                </SubHeading>

                <Flex.By amount={1} />

                <ApplyOrCancelControls
                  canApply={form.formState.isValid && form.formState.isDirty}
                  cancelUri={cancelUri}
                  onCancelled={trackEditCancel}
                />
              </Flex.Row>

              <FormItem
                label="Assigned to"
                labelStyle={{ width: '16.25rem', paddingLeft: '0.75rem' }}
                specialAction={
                  isProjectAdmin ? (
                    <Tooltip title="Edit assignees">
                      <ColourButton.BasicBorderless
                        id="EditAssigneesButton"
                        css={{
                          'width': 'fit-content',
                          'padding': '1px 5px',
                          'minHeight': '28px',
                          'color': colours.TEAL,
                          '&:hover': {
                            color: colours.TEAL_HOVER,
                            backgroundColor: colours.LIGHT_TEAL
                          }
                        }}
                        type="button"
                        onClick={() => openActionStrip('assignees')}
                      >
                        <EditPencil />
                      </ColourButton.BasicBorderless>
                    </Tooltip>
                  ) : null
                }
              >
                <Flex.Row amount={0} verticalCentre>
                  {assignedTo ? assignedTo : <>&nbsp;</>}
                </Flex.Row>
              </FormItem>

              <FormItem
                label="Tags"
                labelStyle={{ width: '16.25rem', paddingLeft: '0.75rem' }}
                specialAction={
                  <Tooltip title="Edit tags">
                    <ColourButton.BasicBorderless
                      id="EditTagsButton"
                      css={{
                        'width': 'fit-content',
                        'padding': '1px 5px',
                        'minHeight': '28px',
                        'color': colours.TEAL,
                        '&:hover': {
                          color: colours.TEAL_HOVER,
                          backgroundColor: colours.LIGHT_TEAL
                        }
                      }}
                      type="button"
                      onClick={() => openActionStrip('tags')}
                    >
                      <EditPencil />
                    </ColourButton.BasicBorderless>
                  </Tooltip>
                }
              >
                <Flex.Row amount={0} verticalCentre>
                  {recordDetail.item?.tags &&
                  recordDetail.item.tags.length > 0 ? (
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}
                    >
                      {recordDetail.item.tags.map(t => (
                        <Tag
                          key={t}
                          color={colours.PALE_TEAL}
                          bordered={false}
                          style={{ margin: 0, color: colours.CORE_NAVY }}
                        >
                          {t}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <>&nbsp;</>
                  )}
                </Flex.Row>
              </FormItem>

              <InfractionsContext infractionsByFieldName={infractionsByName}>
                <ModuleView />
              </InfractionsContext>
            </Form>
          </FormProvider>
        </MainSection>
      )}
    </LimitWidthContainer>
  )
}

RecordEdit.propTypes = {
  doneUri: PropTypes.string.isRequired
}

function useEditCompleteRedirection(doneUri) {
  const navigate = useNavigate()

  return {
    onEditSaveComplete: () => {
      navigate(doneUri)
    },
    cancelUri: doneUri ?? undefined
  }
}

function useFormWithDataAndTracking(ready, data) {
  const { moduleName } = useModuleIds()
  const moduleKey = moduleName.toLowerCase()
  const ModuleView = MODULE_VIEWS[moduleKey]

  const form = useForm({ defaultValues: {}, mode: 'onBlur' })
  const { reset } = form

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )

  const { trackEditOpen, trackEditCancel, trackEditSave, setInitialValues } =
    useMixpanelEvents(form)

  useMountEffect(trackEditOpen)

  useEffect(() => {
    if (ready && data) {
      let formData = data
      if (typeof ModuleView.transformToFormData === 'function') {
        formData = ModuleView.transformToFormData(data, projectTimeZone)
      }
      formData = _.mapValues(formData, value => (value === 0 ? '0' : value))

      reset(formData)
      setInitialValues(formData)
    }
  }, [ModuleView, data, projectTimeZone, ready, reset, setInitialValues])

  return {
    form,
    trackEditSave,
    trackEditCancel
  }
}

const Form = styled.form`
  width: 100%;
  max-width: 80rem;

  align-self: center;
`
